'use strict';
// Because the scroll-to-refresh interval is on a short cycle, we are going to
// skip writing a similar Interval wrapper for pause/cancel/delay.
import Timer from '../../lib/timer.js';
const { getDependencyClass } = require('../../lib/dependencies.js');
const callbackQueuePromise = require('../../lib/callbackQueuePromise');
const performanceMeasure = require('../../lib/performanceMeasure.js');
const getLazyLoadingConfig = require('../../lib/lazyLoadingConfig');
const { debounceWithTime, makeIntersectionObserver } = require('../../lib/util');
let measure = performanceMeasure('webcomponents:ad');

// A set of sizes which we use to show the close button since they are standard
// sizes and do not bring in their own behavior
const SHOW_CLOSE_BUTTON_SIZES = [
    '1x1',
    '301x50',
    '321x50',
    '729x90',
    '971x90',
    '300x251',
    '300x601',
    '160x601',
];

const SCROLL_REFRESH_MIN_TIMEOUT = 4000;
const SCROLL_REFRESH_AUCTION_TIMEOUT = 2000;

const MIN_DEAL_REFRESH_TIME = 30;
const TRUSTED_DEAL_BIDDERS = ['appnexus', 'ix', 'rubicon'];

// Number of dynamically loaded ads to limit on iPhone devices (ADS-6204, ADS-6195)
const IPHONE_AD_LIMIT = 40;

const DEFER_ATTR = 'defer-until-event';
const IS_HIDDEN = 'is-hidden';

// Milliseconds to wait before loading a replacement ad for a dynamic ad
// slot (also requires the ad to go out of the viewport and re-enter).
const DYNAMIC_REFRESH_DELAY = 10000;

// Sane minimum -- 568 is iPhone 5-sized.
const MIN_VIEWPORT_HEIGHT = 568;

// Period to retain 'sticky' position for a dynamic in-content ad
const STICKY_AD_TIMEOUT = 5000;

// Special error instance for indicating no ads are in view
const ENO_ADS_IN_VIEW = new Error('No eligible ads in view');

getDependencyClass('phoenix-page').then((pageClass) => {
    /**
     * InContentAdManager
     *
     * The purpose of this class is to provide a controller for handling one or more
     * in-content ads. Each ad will require an auction and ad request, but only as
     * needed. There could be dozens of in-content ads on a given page, and we want
     * to only run an auction for a subset of ads, and optimize for the subset that
     * are most likely to be rendered. This manager will receive any PhoenixAd element
     * via the `addAd` method. Once supplied, the ad manager will include it in the
     * list of observed targets. It will use the callback to keep track of the ads
     * that are in view or just outside the viewport. Once the page has "settled"
     * and isn't in motion, an auction will run if there are unfilled ads within
     * range of the viewport, and subsequent to the auction, an ad request will fire
     * for those ad slots. For now, this is only utilized for in-content ads, and
     * when the `use-deferred-in-content-ads` feature flag is enabled.
     *
     * If the `use-sticky-in-content-ads` feature flag is enabled, an in-content
     * ad can be given a `is-sticky` CSS class, which will cause it to stick to
     * the top or bottom of the viewport for a configurable duration. After that
     * duration has elapsed, it will return to its original position in the content.
     */
    class InContentAdManager {
        constructor() {
            this.auctionIndex = 0;

            const isIPhone = navigator.userAgent.includes('iPhone OS');

            // Special condition to limit the number of in-content ads we load
            // specifically on iPhone devices. Excessive ads can cause the page
            // to crash, forcing a reload of the page. See ADS-6204, ADS-6195
            this.limitAds = isIPhone;
            this.adsRendered = 0;

            // A set of PhoenixAd instances that are within the viewport range.
            // The IntersectionObserver set up below will manage the contents
            // of this set.
            this.adsInView = new Set();

            // Keeps track of ads that have been auctioned already.
            this.adsAuctioned = new Set();

            // Wait for 300ms after a scroll event before running an auction. We
            // watch for scroll events in addition to intersection events, since this
            // routine is debounced, and continued scrolling will delay the execution
            // of the auction/ad request until the scrolling stops.
            this.queueTrigger = pageClass.util.debounce(this.runAuction.bind(this), 300);

            // Ensure advertising, adAuction, config instances are available
            // before running any dynamic auctions/ad requests. This initial
            // runPromise is fired first, and then subsequent executions
            // follow it.
            this.runPromise = Promise.all(
                ['phxAdvertisingQueue', 'phxAdAuctionQueue', 'phxConfigQueue'].map((queue) =>
                    callbackQueuePromise(queue)
                )
            ).then(([advertising, adAuction, config]) => {
                // If deferred in-content ads are disabled completely, then
                // we can stop here, since all in-content ads should be included
                // in the initial page auction/ad request.

                this.advertising = advertising;
                this.adAuction = adAuction;
                this.breakpoint = config.breakpoint.get();
                this.useStickyInContentAds = config.features.isEnabled('use-sticky-in-content-ads');

                // For iPhone devices, unload ads once they exit the viewport when feature
                // flag is enabled.
                this.unloadAds = isIPhone && config.features.isEnabled('unload-oov-ads');

                this.setupObserver();

                window.addEventListener('scroll', this.queueTrigger);
            });
        }

        setupObserver() {
            const viewportHeight = Math.max(MIN_VIEWPORT_HEIGHT, window.innerHeight);

            // This defines the threshold to go by for the sticky in-content ad
            // feature. If an ad is greater than this, we will not apply a
            // 'sticky' behavior for it.
            this.stickyAdHeightThreshold = Math.ceil(viewportHeight * 0.33);

            let viewportMargin = 0;
            const lazyConfig = getLazyLoadingConfig();
            // See GPT documentation for lazy configuration thresholds:
            // https://developers.google.com/publisher-tag/reference#googletag.PubAdsService_enableLazyLoad

            // Our observer is only concerned with fetchMarginPercent threshold, since it
            // triggers the auction/ad request. The renderMarginPercent is already configured
            // with GPT and GPT is responsible for handling ad rendering.
            viewportMargin = Math.ceil(
                viewportHeight *
                    ((lazyConfig?.thresholds?.fetchMarginPercent ?? 300) / 100) *
                    // apply mobile scaling if we're on the mobile (A) breakpoint
                    (this.breakpoint === 'A' ? lazyConfig?.thresholds?.mobileScaling ?? 3 : 1)
            );

            // Creates an intersection observer that fires for ads that are within
            // `viewportMargin` of the viewport, triggering an auction and ad request.
            this.observer = makeIntersectionObserver(this.onIntersection.bind(this), {
                root: document,
                rootMargin: `${viewportMargin}px 0px`,
            });
            if (this.unloadAds) {
                // This observer triggers when an observed element is 1/2 viewport out of view.
                this.unloadObserver = makeIntersectionObserver(this.onUnloadIntersection.bind(this), {
                    root: document,
                    rootMargin: `${viewportHeight / 2}px 0px`,
                });
            }
        }

        runAuction() {
            // Find ads that are in view, not filled, and not paused, and not already auctioned
            const adsToFill = [...this.adsInView.values()].filter(
                (ad) => !ad.isFilled && !ad.isPaused && !this.adsAuctioned.has(ad.config.slotId)
            );

            // No ads in view are eligible for an auction/render, so simply return
            if (!adsToFill.length) {
                return;
            }

            // If we have reached the limit of ads to dynamically load, prevent
            // this and future auction/renders from happening.
            if (this.limitAds && this.adsRendered >= IPHONE_AD_LIMIT) {
                adsToFill.forEach((ad) => this.unloadAd(ad));
                return;
            }

            const auctionId = 'dynamic-in_content-' + this.auctionIndex++;

            // Prepare the auction configuration: slot configuration, header bidding
            // units, and unit vendor configuration.
            const slots = adsToFill.map((ad) => ad.config.slotConfig);
            const units = adsToFill.reduce((accum, ad) => accum.concat(ad.config.units[this.breakpoint]), []);
            const unitVendorConfig = adsToFill.reduce(
                (accum, ad) => Object.assign(accum, ad.config.unitVendorConfig || {}),
                {}
            );

            const adConfig = {
                slots,
                unitVendorConfig,
                units: { [this.breakpoint]: units },
            };

            // Prevent this ad from being auctioned again
            adsToFill.forEach((ad) => this.adsAuctioned.add(ad.config.slotId));

            // Return a promise that will always resolve, even if there is a failure, so we
            // can continue to chain for the next set of ads.
            return new Promise((resolve) => {
                // Chain here is: dynamic auction -> dynamic ad request -> ad render -> sticky ad handling
                this.adAuction
                    .dynamicAuction(adConfig, auctionId)
                    .catch((e) => {
                        // promise chain failure; perhaps due to an auction/ad request failure.
                        // eslint-disable-next-line no-console
                        console.error('Dynamic ad request exception', e);
                    })
                    .finally(() => {
                        this.advertising.dynamicCall({ auctionId, slots: adConfig.slots });

                        // Attach to each slot's slotRenderEnded event so we can
                        // provide the ad result to the ad component, and apply the
                        // sticky class if that feature is enabled.
                        adsToFill.map((ad) => {
                            this.advertising.getSlotResult(ad.config.slotId).then((adResult) => {
                                this.adsRendered++;
                                ad.handleAd(adResult);
                                if (this.useStickyInContentAds) {
                                    this.applyStickyClass(ad);
                                }
                            });
                        });
                    });
            });
        }

        /**
         * Handles the assignment of a `is-sticky` class to the ad row
         * for the ad provided. The class is removed after a period of
         * time has elapsed. If the ad has been scrolled out of view,
         * it will also apply a `is-sticky-animate-out` class, wait for
         * the animation to run, and then removes the sticky classes from
         * the element.
         * @param {PhoenixAd} ad
         */
        async applyStickyClass(ad) {
            const applyStickyAd = () => {
                const adRow = ad.closest('.m-in-content-ad-row');
                const adRect = adRow.getBoundingClientRect();
                if (adRect.height > this.stickyAdHeightThreshold) {
                    return;
                }
                const distToOffscreen = adRect.top + adRect.height;
                const startOffset = window.scrollY;
                adRow.classList.add('is-sticky');
                let hovering = false;
                const mouseEnter = (e) => (hovering = true);
                const mouseLeave = (e) => (hovering = false);
                // Even if the user is hovering, remove the sticky ad after 30 seconds regardless.
                let patienceTime = 30000 - STICKY_AD_TIMEOUT;
                const popper = () => {
                    if (patienceTime > 0 && hovering) {
                        // lose patience
                        patienceTime -= 500;
                        window.setTimeout(popper, 500);
                        return;
                    }
                    // Check to see if the ad is in view. If it is, just remove 'is-sticky' immediately.
                    // If it is above the viewport, animate it out of view.
                    if (window.scrollY > startOffset && window.scrollY - startOffset >= distToOffscreen) {
                        adRow.classList.add('is-sticky-animate-out');
                        window.setTimeout(() => {
                            adRow.classList.remove('is-sticky', 'is-sticky-animate-out');
                        }, 500);
                    } else {
                        adRow.classList.remove('is-sticky');
                    }
                    adRow.removeEventListener('mouseenter', mouseEnter);
                    adRow.removeEventListener('mouseleave', mouseLeave);
                };
                adRow.addEventListener('mouseenter', mouseEnter, false);
                adRow.addEventListener('mouseleave', mouseLeave, false);
                window.setTimeout(popper, STICKY_AD_TIMEOUT);
            };

            // Poll for ad loaded state so we can attach a sticky class.
            const waitToSettle = () => {
                if (ad.classList.contains('is-loaded') && ad.inView) {
                    applyStickyAd();
                } else if (ad.classList.contains('is-empty')) {
                    // no ad; give up
                } else {
                    setTimeout(waitToSettle, 100);
                }
            };
            setTimeout(waitToSettle, 100);
        }

        onIntersection(entries) {
            const now = new Date().getTime();
            entries.forEach((entry) => {
                const ad = entry.target;
                if (entry.isIntersecting) {
                    ad.isPaused = false;
                    this.adsInView.add(ad);
                } else {
                    if (ad.isFilled && !ad._outOfViewTime) {
                        ad._outOfViewTime = now;
                        ad.isPaused = true;
                    }
                    this.adsInView.delete(ad);
                }
            });

            // Since intersection events occurred, also use the
            // queueTrigger function for checking the ads in view,
            // running an auction, etc.
            this.queueTrigger();
        }

        onUnloadIntersection(entries) {
            entries.forEach((entry) => {
                const ad = entry.target;
                if (!entry.isIntersecting && ad.isFilled && !ad.unloaded) {
                    this.unloadAd(ad);
                }
            });
        }

        addAd(ad) {
            this.runPromise.then(() => {
                if (this.observer) {
                    this.observer.observe(ad);
                }
                if (this.unloadObserver) {
                    this.unloadObserver.observe(ad);
                }

                // We'll also track visibility so the inView property is usable for
                // InContentAdManager when sticky ads are enabled.
                if (this.useStickyInContentAds) {
                    ad.observeVisibilityChanges();
                }
            });
        }

        unloadAd(ad) {
            if (this.observer) {
                this.observer.unobserve(ad);
            }
            if (this.unloadObserver) {
                this.unloadObserver.unobserve(ad);
            }
            this.adsInView.delete(ad);
            // Pin height of slot to prevent changing
            // as it unloads.
            ad.style.height = ad.getBoundingClientRect().height + 'px';
            // Unload ad resource to free up memory
            ad._unload();
            ad.innerHTML = '';
        }
    }

    const inContentAdManager = new InContentAdManager();

    /**
     * @class
     * PhoenixAd
     *
     * @classdesc This web component encapsulates the functionality of the
     * "phoenix-ad" tag to inject and reload Ads based on the
     * provided Ad Config
     *
     * Pug Mixin: server/web/partials/phoenixAd.pug
     * @see {@link server/web/partials/phoenixAd.pug|Pug file}
     *
     * @example
     *      // Basic Ad: "reload" attribute is updated by an external
     *      // component (eg. reload ad after user changes the gallery slide)
     *      <phoenix-ad config='{"json": "example"}'></phoenix-ad>
     *
     * @example
     *      // Auto Ad Reloader: "reload" attribute is updated based on the
     *      // frequency and delay defined in Ad Config
     *      // (eg. Fixed Bottom Ads)
     *      <phoenix-ad config='{"json": "example"}'
     *          auto-reload=true>
     *      </phoenix-ad>
     *
     * @example
     *      // Skip Dynamic Call: The initial load event will not trigger an
     *      // load event. (Note that subquent reloads, if there is an ad in
     *      // the slot, will be handled by the advertising queue reloadAd()
     *      // method and is unaffected by this behavior.
     *      <phoenix-ad config='{"json": "example"}'
     *          skip-dynamic-call
     *      </phoenix-ad>
     *
     * @example
     *      // Defer Until Event: block a dynamic ad from loading pending
     *      // a client-side event. (The value should be a space-separate list
     *      // of event names which will trigger the dynamic call.)
     *      <phoenix-ad config='{"json": "example"}'
     *          defer-until-event="phoenix:fooComponent:eventBar phoenix:fooComponent:eventBaz">
     *      </phoenix-ad>
     *
     * @example
     *      // Specified slot class and dimensions: The "container-height" and
     *      // "container-width" values will be be passed through to the container
     *      // as a style attribute. The "container-class" attribute will be passed
     *      // through as additional class names.
     *      //
     *      // Note that this applies only when the slot and container are
     *      // created by phoenix-ad.
     *      <phoenix-ad config='{"json": "example"}'
     *          container-height=31
     *          container-width=88
     *          container-class="is-aligned-center"
     *      >
     *      </phoenix-ad>
     *
     */
    class PhoenixAd extends pageClass.PhoenixElement {
        /**
         * Defines what attributes will be monitored.
         *
         * @return {Array} Array of strings containing attribute names
         * to observe
         */
        static get observedAttributes() {
            return ['auto-reload-paused'];
        }

        /**
         * Defines what broadcast events will be monitored.
         *
         * @return {Array} Array of strings containing event names
         * to observe
         */
        get observedBroadcastedEvents() {
            let observedBroadcastedEvents = this.getAttribute(DEFER_ATTR)
                ? this.getAttribute(DEFER_ATTR).split(' ')
                : [];

            return [...observedBroadcastedEvents, 'phoenix:fixedBottomAd:hide', 'phoenix:fixedBottomAd:show'];
        }

        /**
         * Handler for broadcasted events.
         */
        broadcastedEventCallback(name, args = {}) {
            if (name && this.deferUntilEvent && this.deferUntilEvent.indexOf(name) > -1) {
                this._load();
                // Disable deferUntilEvent going forward
                this.deferUntilEvent = false;
            } else {
                switch (name) {
                    case 'phoenix:fixedBottomAd:hide':
                        if (this.fixedVisible && this.firstChild) {
                            this.firstChild.classList.add('is-offscreen-bottom');
                            this.inView = false;
                            this.isPaused = true;
                            this._debouncedVisibilityChange(false);
                        }
                        break;
                    case 'phoenix:fixedBottomAd:show':
                        if (this.fixedVisible && this.firstChild) {
                            this.firstChild.classList.remove('is-offscreen-bottom');
                            this.inView = true;
                            this.isPaused = false;
                            this._debouncedVisibilityChange(true);
                        }
                        break;
                }
            }
        }

        /**
         * Check if this element or any of its parent is hidden
         * @return {boolean}
         */
        get isHidden() {
            // HTMLElement.offsetParent property will return null whenever it,
            // or any of its parents is hidden. We force isHidden() to false if
            // the "fixed visible" attribute is set, indicating an ad which is
            // always treated as visible, such as fixedBottom.
            return !this.fixedVisible && !this.offsetParent;
        }

        /**
         * Add/Remove 'auto-reload-paused' attribute.
         */
        set isPaused(val) {
            // In future this can also be set by an external
            // component like `phoenix-page` when `offset menu` is opened
            // or Ad take-over is on.

            if (val) {
                this.setAttribute('auto-reload-paused', val);
            } else {
                this.removeAttribute('auto-reload-paused');
            }
        }

        /**
         * Check if the element has 'auto-reload-paused' attribute.
         * @return {boolean}
         */
        get isPaused() {
            return this.hasAttribute('auto-reload-paused');
        }

        /**
         * @access public
         * Get 'reloadCount' value.
         * @return {number}
         */
        get reloadCount() {
            return this._reloadCount;
        }

        connectedCallback() {
            super.connectedCallback(); // set up broadcasted event listeners

            this.autoReload = this.hasAttribute('auto-reload');
            this.closeButton = this.hasAttribute('close-button');
            this.deferUntilEvent = this.getAttr(DEFER_ATTR) ? this.getAttr(DEFER_ATTR).split(' ') : false;
            this.fixedVisible = this.hasAttribute('fixed-visible');
            this.skipDynamicCall = this.hasAttribute('skip-dynamic-call');
            this.scrollToRefreshThreshold = this.getAttr('scroll-to-refresh');
            this.skipScrollToRefresh = false;
            this.initialIndex = this.getAttr('initial-index');
            this.isFilled = false;
            this.recirculationOpen = !!document.querySelector('.salish-recirculation-open');
            // We currently support only integer values for container dimensions.
            let containerHeight = parseInt(this.getAttr('container-height'));
            // Address the NaN case from an invalid parse, as well as negative
            // values caused by some sort of edge case.
            if (containerHeight && Number.isInteger(containerHeight) && containerHeight > 0) {
                this.containerHeight = containerHeight;
            }
            let containerWidth = parseInt(this.getAttr('container-width'));
            if (containerWidth && Number.isInteger(containerWidth) && containerWidth > 0) {
                this.containerWidth = containerWidth;
            }
            this.containerClass = this.getAttr('container-class');

            if (!this.hasAttribute('config')) {
                throw new Error('No ad configuration was specified.');
            }

            let self = this;
            if (!window.phxConfigQueue) {
                window.phxConfigQueue = [];
            }
            window.phxConfigQueue.push(function () {
                self.breakpoint = this.breakpoint.get();
            });

            // Require the ad to come into view once, before we start reporting its viewability,
            // to prevent all ads from emitting an event when IntersectionObserver is initialized.
            this._visibleOnce = false;
            this._debouncedVisibilityChange = debounceWithTime(this._trackVisibilityChange.bind(this), 500);

            this.inView = false;

            this.handleAd = this._handleAd.bind(this);
            this.adLoadedTime = 0;
            this._reloadCount = 0;

            this.adAuctionPromise = callbackQueuePromise('phxAdAuctionQueue');
            window.phxAdvertisingQueue = window.phxAdvertisingQueue || [];
            this.deferLoad = this.config.slotConfig?.model?.deferLoad;
            this._initListeners();

            if (this.config.slotId && !this.hasChildNodes()) {
                this._addSlotDiv(this.config.slotId);
            }

            if (this.initialIndex && this.config.slotConfig && this.config.slotConfig.model) {
                this.config.slotConfig.model.index = parseInt(this.initialIndex);
            }

            pageClass.util.whenPageLoaded().then(() => {
                if (!this.config.slotId || !this.config.slotConfig) {
                    throw new Error('slot id and/or slot config is missing');
                }
                // Force the maxAdReload to be 1 if the special -1 delay has been passed
                if (this.maxAdReloads === undefined) {
                    this.maxAdReloads =
                        this.config.adReloaderDelay === -1 ? 1 : this.config.maxAdReloads || 25;
                }
                if (!this.deferLoad && !this.deferUntilEvent) {
                    this._load();
                }
            });
        }

        /**
         * Detects when any of the attributes returned by the "observedAttributes"
         * getter gets updated, added or deleted. Note that this is a standard
         * method of custom components.
         *
         * @param {String} name Name of the attribute that was updated
         * @param {String} oldValue Value that was replaced/removed
         * @param {String} newValue Value that was set/added
         */
        attributeChangedCallback(name, oldValue, newValue) {
            if (name === 'auto-reload-paused' && this.reloadTimer) {
                // Currently, this attribute is changed based on
                // whether or not the element is in viewport or
                // by takeoveropened/ takeoverclosed events
                if (this.isPaused) {
                    this.reloadTimer.pause();
                    this.prebidReloadTimer.pause();
                    clearInterval(this.s2RInterval);
                } else {
                    this.reloadTimer.resume();
                    this.prebidReloadTimer.resume();
                    if (this.scrollToRefreshThreshold) {
                        const handler = this._scrollToRefreshTickHandler.bind(this);
                        this.s2RInterval = setInterval(handler, 500);
                    }
                }
            }
        }

        /**
         * @access public
         * @param {String} prebidAuctionId Value identifying the prebid
         *      auction for this ad, previously generated. This will be set
         *      to false if it has expired.
         */
        reload(prebidAuctionId) {
            const reloadCount = this._reloadCount;
            const slotConfig = this.config.slotConfig;

            if (reloadCount < this.maxAdReloads && !this.isPaused) {
                // Check if the initial Ad markup is present,
                // this will only happen when this component was
                // hidden or was not present while initial Ad bootstrapping
                // (e.g.: Long-form gallery interstitial Ad )
                if (!this.hasChildNodes()) {
                    this._addSlotDiv();
                    this._load();
                } else {
                    const handler = this.handleAd;
                    const currentTime = new Date().getTime();

                    if (this.prebidTime && (currentTime - this.prebidTime) / 1000 > 30) {
                        // Our prebid is stale, possibly due to pause/resume
                        // timing issues. Kill the prebidAuctionId and refresh
                        // without it.
                        prebidAuctionId = false;
                    }

                    window.phxAdvertisingQueue.push(function () {
                        // context of `this` is `advertising` here; the
                        // returned result from reloadAd is an array of
                        // slotconfig objects.
                        this.reloadAd(slotConfig, prebidAuctionId).then((result = []) => {
                            result.map(handler);
                        });
                    });
                }

                if (this.autoReload && this.isFilled) {
                    // reset Load Timer
                    this._setLoadTimer();
                }

                // Before we update the ad index, we need to track that
                // this ad index is no longer visible, so we issue a
                // tracking event here. Note that we don't use the debounced
                // version of the visibility change function here, because
                // we want to track the visibility change immediately.
                this._trackVisibilityChange(false);

                // This is the actual adindex increment. See _updateAdIndexForPrebid() where this value
                // has been pre-given to prebid
                if (this.config.slotConfig.model && typeof this.config.slotConfig.model.index === 'number') {
                    this.config.slotConfig.model.index++;
                }

                this._reloadCount++;
                if (this._reloadCount > this.maxAdReloads) {
                    this.cancelReloading();
                }
            }
        }

        _handleAd(adResult) {
            // Inspect an incoming ad, then decide what the state of our
            // reloader is based on what size it is.
            //
            // If we're on a size that
            // matches our "magic sizes", we want to disable the reloader.
            // If we don't recognize the size (such as "2x2") we want to
            // disable the reloader *and* tear down our container if any.
            //
            // There is additional logic for the special case of fixed bottom
            // on breakpoint A.
            const slotConfig = this.config.slotConfig;

            if (!adResult.filled) {
                // Nothing to do with this, so just try again.
                this.isFilled = false;
                this._setLoadTimer();
                return;
            }

            const adSize = adResult.sizeDims;
            let prebidDeal = false;
            if (window.pbjs?.getAdserverTargetingForAdUnitCode) {
                // If we have a PrebidJS object, let's make sure we don't have
                // an `hb_deal` key, which overrides our reloading logic.
                const targeting = window.pbjs.getAdserverTargetingForAdUnitCode(this.config.slotId) || {};
                if (TRUSTED_DEAL_BIDDERS.indexOf(targeting['hb_bidder']) > -1) {
                    prebidDeal = targeting['hb_deal'];
                }
            }
            this.skipScrollToRefresh = false;
            // If the winner was a deal make sure we satisfy the minimum reload
            // time for deals and temporarily disable scroll to refresh
            if (prebidDeal) {
                this.reloadDelay = Math.max(this.reloadDelay, MIN_DEAL_REFRESH_TIME);
                this.skipScrollToRefresh = true;
            }

            // Get Ad sizes from requested Ads array
            //
            // `getRequestedSizes` covers the following Ads Size array combinations
            // 1. [300, 600]
            // 2. [[300, 250],[300, 600]] // Multidimensional
            // 3. ['test', [300, 200]]
            // 4. ['test', 'test2'];
            function getRequestedSizes(arr) {
                let sizes = [];
                // Check if size Array is Multidimensional
                if (arr.some(Array.isArray)) {
                    sizes = arr.reduce((adSizes, size) => {
                        adSizes.push(Array.isArray(size) ? size.join('x') : size);
                        return adSizes;
                    }, []);
                } else {
                    if (arr.length === 2 && !arr.some(isNaN)) {
                        sizes.push(arr.join('x'));
                    } else {
                        sizes = arr;
                    }
                }
                return sizes;
            }

            let requestedSizes = [];
            if (slotConfig.config && slotConfig.config.sizes && slotConfig.config.sizes.length) {
                requestedSizes = getRequestedSizes(slotConfig.config.sizes);
            }

            const supportedSizes = requestedSizes.concat(SHOW_CLOSE_BUTTON_SIZES);

            if (supportedSizes.indexOf(adSize) > -1) {
                // If we want a close button and haven't added one yet, add it
                // now.
                if (this.config.slotId && this.closeButton && !this.closeButtonDiv) {
                    this._addCloseButton(this.config.slotId);
                }
            } else {
                this.handleNonstandardSize();
            }

            this.isFilled = true;

            // Cancel any potential visibility change events that might be waiting,
            // and immediately track without the debounce function
            // When an ad is filled, consider that it is in view
            this._debouncedVisibilityChange.cancel();
            this._trackVisibilityChange(true);

            // Are we restored after a RCW closed event?
            if (this.style.display === 'none') {
                this.style.display = '';
                this.inView = true;
            }
            this.adLoadedTime = new Date().getTime();
            // If autoload is now false, cancel our reloaders (timer and scroll
            // to refresh). If autoload is on and the timer's not set yet, go
            // aheaad and set it.
            if (!this.autoReload) {
                this.cancelReloading();
            } else if (!this.reloadTimer) {
                this._setLoadTimer();
            }
            // Run this callback in case the RCW was open before the ad loaded.
            if (slotConfig.model.zone === 'fixed_bottom' && this.recirculationOpen) {
                this._salishRecirculationOpened();
            }
        }

        cancelReloading() {
            if (this.reloadTimer) {
                this.reloadTimer.cancel();
            }
            if (this.prebidReloadTimer) {
                this.prebidReloadTimer.cancel();
            }
            // clearInterval is safe to call on already-cleared intervals or
            // undefined values.
            clearInterval(this.s2RInterval);
        }

        handleNonstandardSize() {
            // We got a size like "2x2" indicating an ad that brings its own
            // behavior. Make any changes required to back out potentially
            // conflicting styles or behaviors that are native to PhoenixAd.
            // Note that this method must be valid for all potential slots to
            // encourage reuse.
            if (this.closeButton) {
                if (this.closeButtonDiv) {
                    this.closeButtonDiv.parentElement.removeChild(this.closeButtonDiv);
                }
                this.closeButton = false;
                this.closeButtonDiv = undefined;
            }
        }

        /**
         * @access private
         * sets up our close button
         */
        _addCloseButton(slotId) {
            this.closeButtonDiv = document.getElementById(slotId).nextElementSibling;
            this.closeButtonDiv.classList.remove(IS_HIDDEN);
            this.closeButtonHandler = () => {
                this.classList.add(IS_HIDDEN);
                // Destroy our listeners and kill any reload timers
                this._unload();
                return false;
            };
            this.closeButtonDiv.addEventListener('click', this.closeButtonHandler);
        }

        /**
         * @access private
         * sets up our ad container and ad slot divs. Note that this is
         * probably going to be refactored away in the future, as PhoenixAd
         * takes over the ad code and _becomes_ the container/slot.
         */
        _addSlotDiv(slotId) {
            // Set up our ad container and add slot.
            const adContainer = document.createElement('div');
            const adSlot = document.createElement('div');
            let classNames = this.config.visClasses || [];
            adSlot.id = slotId;
            if (this.config.moduleClass) {
                classNames.push(this.config.moduleClass);
            }
            classNames.push('is-placeholder');
            adContainer.classList.add(...classNames);

            // Additional container behavior, as for the component badge case.
            if (this.containerClass) {
                let extraClasses = this.containerClass.split(' ');
                adContainer.classList.add(...extraClasses);
            }
            if (this.containerHeight) {
                adContainer.style.height = this.containerHeight + 'px';
            }
            if (this.containerWidth) {
                adContainer.style.width = this.containerWidth + 'px';
            }
            adContainer.appendChild(adSlot);
            this.appendChild(adContainer);
        }

        /**
         * @access private
         * This updates the adindex that the prebid modules will use for logging to be
         * one more than the current value in the slotConfig.model. This is because
         * there can be prebid auctions that are not actually used and we only want to
         * actually increment the adindex when we're going to use the results of the auction
         * but we want any logging from prebid modules to reflect what that adindex will
         * end up being
         */
        _updateAdIndexForPrebid(adAuction) {
            const slotConfig = this.config.slotConfig;
            if (!slotConfig.model || typeof slotConfig.model.index !== 'number') {
                return;
            }

            adAuction.updateZoneIndex(this.config.slotConfig.id, {
                ...slotConfig.model,
                index: slotConfig.model.index + 1,
            });
        }

        _scrollToRefreshTickHandler() {
            // If we're past our 4-second threshold, check where
            // we are on the page. Note that this is disabled if the ad is
            // hidden.
            if (this.isHidden) {
                return;
            }
            if (this.adLoadedTime && new Date().getTime() - this.adLoadedTime > SCROLL_REFRESH_MIN_TIMEOUT) {
                const { slotConfig } = this.config;
                const viewportHeight = Math.max(MIN_VIEWPORT_HEIGHT, window.innerHeight);
                let distance = (window.pageYOffset - this.lastPageYOffset) / viewportHeight;
                if (Math.abs(distance) >= this.scrollToRefreshThreshold) {
                    // We are in range. If we are more than two seconds away
                    // from the the timer firing, let's kick off an auction
                    // with a two-second timeout. If we're *closer* to the
                    // timer firing, use longer, higher-value auction. Either
                    // way, we can disable the interval.
                    clearInterval(this.s2RInterval);
                    let rT = this.reloadTimer;
                    if (rT && !rT.cancelled && rT.remaining() < SCROLL_REFRESH_AUCTION_TIMEOUT) {
                        this.lastPageYOffset = window.pageYOffset;
                    } else {
                        // Disable our timers while the S2R auction runs.
                        this.cancelReloading();
                        const s2RAuctionId = 's2r-' + slotConfig.id + '-' + this._reloadCount;
                        // The auction may have run before we canceled the timer.
                        if (!this.prebidReloadTimer?.called) {
                            let self = this;
                            // Run an auction
                            this.adAuctionPromise
                                .then(function (adAuction) {
                                    self._updateAdIndexForPrebid(adAuction);
                                    adAuction.rerunAuction(
                                        slotConfig.id,
                                        SCROLL_REFRESH_AUCTION_TIMEOUT,
                                        s2RAuctionId
                                    );
                                })
                                .catch(function (error) {
                                    // eslint-disable-next-line no-console
                                    console.warn('Scroll to refresh auction failed: ' + error);
                                });
                        }
                        // Immediately after the auction is done load the ad.
                        this.reloadTimer = new Timer(() => {
                            this.reload(s2RAuctionId);
                        }, SCROLL_REFRESH_AUCTION_TIMEOUT + 10);
                        this.lastPageYOffset = window.pageYOffset;
                    }
                }
            }
        }

        _load() {
            const slotConfig = this.config.slotConfig;
            const handleAd = this.handleAd;
            // The initial load needs to obey the same transformation rules as
            // subsequent reloads. We do not use fat arrow notation, so that we
            // can get access to the advertising queue inside the function.
            window.phxAdvertisingQueue.push(function () {
                // Take our initial ad, if any, and apply our ad
                // reloader behavior to it -- this will be in charge
                // of setting our timer after we see our initial ad,
                // in case we get an ad size back that disables reload.
                this.getSlotResult(slotConfig.id).then(handleAd);
            });
            if (!this.skipDynamicCall) {
                // Make Initial Ad Call
                window.phxAdvertisingQueue.push(function () {
                    // context of `this` is `advertising` here
                    this.dynamicCall({ slots: [slotConfig] });
                });
            }

            // Initiate Auto Reload functionality
            if (this.autoReload) {
                // This value can't be less than 30, as limited by the server
                this.reloadDelay = this.config.adReloaderDelay || 30;

                // We don't want to track visibility for fixed ads, since they
                // are always visible, and doing this breaks the ad reloader
                if (this.fixedVisible) {
                    return;
                }
            }

            this.observeVisibilityChanges();
        }

        onChangeToVisible() {
            this.inView = true;
            this._visibleOnce = true;
            this._debouncedVisibilityChange(true);

            if (this.autoReload) {
                this.isPaused = false;
                if (this.isFilled) {
                    // Reset 'load' time for viewability measures
                    this.adLoadedTime = new Date().getTime();
                }
            }
        }

        onChangeToNotVisible() {
            this.inView = false;

            // If the debounce function is pending, cancel it and don't report anything
            // as we want to avoid tracking when an ad appears for less time it takes
            // for the debounce to fire
            if (this._debouncedVisibilityChange.pending()) {
                this._debouncedVisibilityChange.cancel();
            } else {
                this._debouncedVisibilityChange(false);
            }

            if (this.autoReload) {
                this.isPaused = true;
            }
        }

        _initListeners() {
            this.callbacks = this._bindCallbacks();
            // Listen to the broadcasted message (takeoveropened/takeoverclosed)
            // to pause/ resume reloader timer
            window.addEventListener('message', this.callbacks.takeover, false);

            // Listen to document visibility to pause/ resume auto reloader timer
            document.addEventListener('visibilitychange', this.callbacks.browserTabVisibility);

            // Listen to photo gallery state to pause/ resume auto reloader timer
            document.addEventListener('phxCollection:enterFullscreen', this.callbacks.galleryOpened);
            document.addEventListener('phxCollection:exitFullscreen', this.callbacks.galleryClosed);

            // We only respond to any Salish Recirculation Widget events for the fixed-visible attribute
            // use of phoenix-ad (used exclusively for the fixed_bottom zone).
            if (this.fixedVisible) {
                document.addEventListener(
                    'salishRecirculation:opened',
                    this.callbacks.salishRecirculationOpened
                );
                document.addEventListener(
                    'salishRecirculation:closed',
                    this.callbacks.salishRecirculationClosed
                );
            }

            // In-content ads that are specifying a deferred load will utilize the
            // inContentAdManager to load the ad when the user scrolls into view.
            // This controller handles multiple phoenix-ad instances, and will
            // issue an auction/ad request for a set of ads that are in view or
            // near the viewport.
            // Please keep this list in sync with the one in web/lib/adConfigUtil.js
            if (
                this.deferLoad &&
                ['in_content', 'in_card', 'below_content_card'].includes(this.config.slotConfig.model.zone)
            ) {
                inContentAdManager.addAd(this);
            }
        }

        _setLoadTimer() {
            const adAuctionPromise = this.adAuctionPromise;
            const slotConfig = this.config.slotConfig;

            if (!slotConfig || !slotConfig.id) {
                // eslint-disable-next-line no-console
                console.warn('Malformed ad slot config missing id; reload timer cancelled.');
                return;
            }

            this.cancelReloading();

            if (!this.autoReload || this.reloadDelay <= 0) {
                return;
            }
            const prebidAuctionId = slotConfig.id + '-' + this._reloadCount;

            // We want to issue a prebid auction for this refresh as early as
            // possible, but we're also mindful of a TTL for bids which can
            // be as low as 35 seconds. so to be safe, we will at most do a
            // prebid auction 30 seconds prior to the ad call. The prebidTimeout
            // is for the auction results, which we want to time at 3 seconds
            // prior to the ad call.
            // Example: if the reloadDelay is 30, we don't want to defer the
            // auction at all, so prebidDelay winds up being 0 seconds.
            // The timeout would be 27 seconds, since we don't want a
            // negative timeout.
            // If our reloadDelay is longer, say 90 seconds, we'd calculate
            // a prebidDelay of 60 seconds; the prebidTimeout would calculate
            // to 27 seconds again (90 - 60 - 3 = 27).
            // If our reloadDelay is short, say 20 seconds, we'd get a prebidDelay
            // of 0. The prebidTimeout would be 17 (20 - 0 - 3 = 17).
            const prebidDelay = Math.max(this.reloadDelay - 30, 0);
            const prebidTimeout = Math.max(this.reloadDelay - prebidDelay - 3, 2);

            this.prebidReloadTimer = new Timer(() => {
                if (this._reloadCount <= this.maxAdReloads && !this.isHidden) {
                    let self = this;
                    adAuctionPromise
                        .then(function (adAuction) {
                            self._updateAdIndexForPrebid(adAuction);
                            adAuction.rerunAuction(slotConfig.id, prebidTimeout * 1000, prebidAuctionId);
                            self.prebidTime = new Date().getTime();
                        })
                        .catch(function (error) {
                            // eslint-disable-next-line no-console
                            console.warn('Prebid auction failed: ' + error);
                        });
                }
            }, prebidDelay * 1000);
            this.reloadTimer = new Timer(() => {
                if (this._reloadCount <= this.maxAdReloads && !this.isHidden) {
                    this.reload(prebidAuctionId);
                }
            }, this.reloadDelay * 1000);

            // If we have a scroll-to-refresh threshold, set up an interval
            // unless currently displayed ad came from a deal in which
            // case we just rely on the timed reload
            if (this.scrollToRefreshThreshold && !this.skipScrollToRefresh) {
                const handler = this._scrollToRefreshTickHandler.bind(this);
                this.lastPageYOffset = window.pageYOffset;
                this.s2RInterval = setInterval(handler, 500);
            }
        }

        _bindCallbacks() {
            return {
                browserTabVisibility: this._browserTabVisibilityCallback.bind(this),
                galleryClosed: this._galleryClosed.bind(this),
                galleryOpened: this._galleryOpened.bind(this),
                salishRecirculationOpened: this._salishRecirculationOpened.bind(this),
                salishRecirculationClosed: this._salishRecirculationClosed.bind(this),
                takeover: this._takeoverCallback.bind(this),
            };
        }

        _takeoverCallback(message) {
            if (message.origin.match(window.location.host)) {
                try {
                    var data = JSON.parse(message.data);
                    if (data.event === 'takeoveropened') {
                        this.isPaused = true;
                    } else if (data.event === 'takeoverclosed' && this.inView) {
                        this.isPaused = false;
                    }
                } catch (e) {}
            }
        }

        _galleryOpened() {
            this.isPaused = true;
        }

        _galleryClosed() {
            if (this.inView) {
                this.isPaused = false;
            }
        }

        get smallScreen() {
            let threshold = this.breakpoint === 'A' ? 600 : 1000;
            return window.innerHeight < threshold;
        }

        _salishRecirculationOpened() {
            let now = new Date().getTime();
            let timeout = Math.max(5000 - (now - this.adLoadedTime), 0);
            // Defer closing for up to 5 seconds to help visibility.
            // If the ad was loaded 5+ seconds ago, this hides immediately.
            this.isPaused = true;
            window.setTimeout(() => {
                this.style.display = 'none';
                this.inView = false;
            }, timeout);
        }

        _salishRecirculationClosed() {
            this.isPaused = false;
            if (this.isFilled) {
                this.style.display = '';
                this.inView = true;
                // Reset 'load' time for viewability measures
                this.adLoadedTime = new Date().getTime();
            }
        }

        _browserTabVisibilityCallback() {
            if (this.inView) {
                const isDocumentVisible = document.visibilityState === 'visible';
                this.isPaused = !isDocumentVisible;
                this._trackVisibilityChange(isDocumentVisible);
            }
        }

        _unload() {
            if (this.unloaded) {
                return;
            }

            this.cancelReloading();
            this.unobserveVisibilityChanges();

            if (this.closeButtonDiv) {
                this.closeButtonDiv.removeEventListener('click', this.closeButtonHandler);
            }

            let slotId = this.config.slotId;
            window.phxAdvertisingQueue.push(function () {
                this.resetAdSlot(slotId);
            });

            // Adding an additional check for `this.callbacks`
            // Incase the element is disconnected before listeners
            // are added, currently this is happening in the side rail
            // where angular is disconnecting this component which is
            // part of the initial side rail markup and connecting it again
            // if there is enough space to show an additional Ad
            if (this.autoReload && this.callbacks) {
                window.removeEventListener('message', this.callbacks.takeover);
                document.removeEventListener('visibilitychange', this.callbacks.browserTabVisibility);
                document.removeEventListener('phxCollection:enterFullscreen', this.callbacks.galleryOpened);
                document.removeEventListener('phxCollection:exitFullscreen', this.callbacks.galleryClosed);
                if (this.fixedVisible) {
                    document.removeEventListener(
                        'salishRecirculation:opened',
                        this.callbacks.salishRecirculationOpened
                    );
                    document.removeEventListener(
                        'salishRecirculation:closed',
                        this.callbacks.salishRecirculationClosed
                    );
                }
            }

            this.unloaded = true;

            // Remove ad row from DOM
            const row = this.closest('.m-in-content-ad-row');
            if (row) {
                row.parentElement.removeChild(row);
            }
        }

        /**
         *
         * @param {Boolean} isVisible Wether the ad became visible or not
         * @param {Number | undefined} dateNow Timestamp of the event
         * @param {Number | undefined} performanceNow performance.now() of the event
         */
        _trackVisibilityChange(isVisible, dateNow, performanceNow) {
            // The ad must come into view at least once before we start tracking
            // visibility changes - this is to prevent all ads from tracking visibility
            // when IntersectionObserver inits, and none of the ads are actually visible
            if (this._visibleOnce && this.config.slotConfig) {
                const {
                    id,
                    model: { index, zone },
                } = this.config.slotConfig;
                this.emitEvent('phoenix:ad:visibility-changed', {
                    bubbles: true,
                    detail: {
                        isVisible,
                        slotElementId: id,
                        adindex: isNaN(+index) ? -1 : +index,
                        adzone: zone,
                        isFilled: this.isFilled,
                        ts: dateNow ?? Date.now(),
                        tspl: (performanceNow ?? window.performance.now()) | 0,
                    },
                });
            }
        }

        disconnectedCallback() {
            super.disconnectedCallback();
            this._unload();
        }
    }

    window.customElements.define('phoenix-ad', PhoenixAd);
    measure.end();
});
